import moment from "moment";
import { AgEventAPI, SearchAPI } from "@ai360/core";
import {
    EventDetails,
    SampleSetup,
    AgEventApplication,
    AgEventHarvest,
    AgEventPlanting,
    AgEventScouting,
} from ".";
import { FieldInfo } from "~/customer-data/models";

export class AgEventSummary {
    activeYn: boolean;
    agEventDisplayName: string;
    agEventGeneralGuid: string;
    agEventGuid: string;
    agEventName: string;
    agEventTransactionTypeGuid: string;
    cropPurposeName: string;
    agEventTypeName: string;
    cropDisplayName: string;
    cropGuids: string[];
    croppingSeasonGuid: string;
    croppingSeasonName: string;
    equipmentDisplayName: string;
    eventDate: string;
    eventId: string;
    gridArea: number;
    fieldGuid: string;
    fieldBoundaryGuid: string;
    fieldName: string;
    fieldAcres: number;
    farmName: string;
    hasSoilSampleResults: boolean;
    importedPoints: number;
    importedStatus: number;
    interpolationType: string;
    isImportedYn: boolean;
    isFromEquationRec: boolean;
    modifiedDate: string;
    modifiedBy: string;
    numberOfSampleDepths: string;
    productMixName: string;
    status: string;
    totalPoints: number;
    calculatedArea: number;
    customerName: string;
    customerGuid: string;
    customerEnrolled: boolean;

    get croppingSeasonNum(): number {
        return Number(this.croppingSeasonName);
    }

    get eventDateParsed(): Date {
        return moment(this.eventDate, "MM-D-YY").toDate();
    }

    static fromJsonObj(
        jsonObj: AgEventAPI.IAgEventSummary,
        field: SearchAPI.IFieldResult
    ): Readonly<AgEventSummary> {
        const { importPoints, ...rvProps } = jsonObj;
        const rv = new AgEventSummary();
        Object.assign(rv, rvProps);
        rv.importedPoints = importPoints?.importedPoints;
        rv.totalPoints = importPoints?.totalPoints ?? 0;
        rv.fieldBoundaryGuid = field.boundaryId;
        rv.fieldName = field.name;
        rv.farmName = field.farmName;
        rv.fieldAcres = field.acres;
        rv.customerName = field.customerName;
        rv.customerGuid = field.customerId;
        rv.customerEnrolled = field.customerEnrolled;
        return Object.freeze(rv);
    }

    public static fromSummaryAndFieldInfo(
        jsonObj: AgEventSummary & {
            importPoints?: { importedPoints?: number; totalPoints?: number };
        },
        field: FieldInfo
    ): Readonly<AgEventSummary> {
        const rv = new AgEventSummary();
        Object.assign(rv, jsonObj);
        rv.importedPoints = jsonObj.importPoints?.importedPoints ?? jsonObj.importedPoints ?? 0;
        rv.totalPoints = jsonObj.importPoints?.totalPoints ?? jsonObj.totalPoints ?? 0;
        rv.fieldBoundaryGuid = field.fieldBoundaryGuid;
        rv.fieldName = field.name;
        rv.farmName = field.farmName;
        rv.fieldAcres = field.acres;
        rv.customerName = field.customerName;
        rv.customerGuid = field.customerGuid;
        rv.customerEnrolled = field.customerEnrolled;
        return Object.freeze(rv);
    }

    static fromEventDetails(
        eventDetails: EventDetails,
        fieldBoundaryGuid: string = null,
        fieldName: string = null,
        farmName: string = null,
        fieldAcres: number = null,
        customerName: string = null,
        croppingSeasonName: string = null,
        importedStaus: number = null,
        importedPoints: number = null,
        hasSoilSampleResults: boolean = null,
        interpolationType: string = null,
        cropData: { guid: string; name: string }[] = null,
        picklistOptionsCropPurpose: { value: string | number; label: string }[] = null
    ): Readonly<AgEventSummary> {
        const rv = new AgEventSummary();
        const typesWithCrop = [
            AgEventApplication,
            AgEventHarvest,
            AgEventPlanting,
            AgEventScouting,
        ];
        rv.activeYn = true;
        rv.agEventGeneralGuid = eventDetails.agEventGeneralGuid;
        rv.agEventTransactionTypeGuid =
            eventDetails.agEventTypeList.length > 0
                ? eventDetails.agEventTypeList[0].agEventTransactionTypeGuid
                : "";
        rv.agEventTypeName = eventDetails.agEventTypeList
            .map((agEventTypeInfo) => agEventTypeInfo.agEventTransactionTypeName)
            .join(" + ");
        rv.croppingSeasonGuid = eventDetails.croppingSeasonGuid;
        rv.croppingSeasonName = croppingSeasonName;
        rv.customerGuid = eventDetails.customerGuid;
        rv.equipmentDisplayName = eventDetails.isTillageEvent
            ? eventDetails.equipmentList?.find(
                  (equipment) => equipment.equipmentParentTypeName === "Tillage"
              )?.equipmentName ?? null
            : null;
        rv.eventDate = eventDetails.momentStartDate.format("M/D/YY");
        rv.agEventName = eventDetails.eventName;
        rv.agEventDisplayName = [rv.agEventTypeName, rv.eventDate, rv.agEventName]
            .filter((val) => val !== "")
            .join(" - ");
        rv.modifiedDate = eventDetails.modifiedDate;
        rv.modifiedBy = eventDetails.modifiedBy;
        rv.fieldGuid = eventDetails.fieldGuid;
        rv.isFromEquationRec = eventDetails.isFromEquationRec;
        rv.calculatedArea = eventDetails.eventAreaList.reduce(
            (accumulator, area) => accumulator + area.calculatedArea,
            0
        );
        rv.hasSoilSampleResults = hasSoilSampleResults ?? false;
        rv.interpolationType = interpolationType ?? "Standard";
        rv.importedStatus = importedStaus ?? 0;
        rv.fieldBoundaryGuid = fieldBoundaryGuid;
        rv.fieldName = fieldName;
        rv.farmName = farmName;
        rv.fieldAcres = fieldAcres;
        rv.customerName = customerName;

        if (
            eventDetails.eventAreaList?.length > 0 &&
            eventDetails.eventAreaList[0].agEventList.length > 0
        ) {
            const agEventModel = eventDetails.eventAreaList[0].agEventList[0].agEventModel;
            if (typesWithCrop.some((type) => agEventModel instanceof type)) {
                const cropGuids = eventDetails.eventAreaList.map((eventArea) =>
                    eventArea.agEventList.map((agEvent) => (agEvent as any).agEventModel.cropGuid)
                );
                rv.cropGuids = cropGuids.flat();
                if (cropData) {
                    const uniqueCropGuids = [...new Set(rv.cropGuids.filter(Boolean))];
                    rv.cropDisplayName = uniqueCropGuids
                        .map((guid) => cropData.find((c) => c.guid === guid)?.name)
                        .filter(Boolean)
                        .join(" + ");
                }
                if (picklistOptionsCropPurpose) {
                    const cropPurposeGuid = (agEventModel as any).cropPurposeGuid;
                    rv.cropPurposeName = picklistOptionsCropPurpose.find(
                        (o) => o.value === cropPurposeGuid
                    )?.label;
                }
            }
            if (agEventModel instanceof SampleSetup) {
                const sampleEvent = agEventModel;
                rv.eventId = sampleEvent.eventId;
                rv.importedPoints = importedPoints ?? 0;
                rv.numberOfSampleDepths = "1";
                rv.totalPoints = sampleEvent.samplePoints.length;
            }
        }

        return Object.freeze(rv);
    }

    compare(rightHandSide: AgEventSummary): number {
        console.assert(rightHandSide instanceof AgEventSummary);
        if (this.croppingSeasonNum !== rightHandSide.croppingSeasonNum) {
            return this.croppingSeasonNum - rightHandSide.croppingSeasonNum;
        }

        const thisEventDate = moment(this.eventDate, "M/D/YY");
        const rhsEventDate = moment(rightHandSide.eventDate, "M/D/YY");

        return thisEventDate < rhsEventDate ? -1 : thisEventDate > rhsEventDate ? 1 : 0;
    }

    updateImportedStatus(importedStatus: number): AgEventSummary {
        const rv = new AgEventSummary();
        Object.assign(rv, this, { importedStatus });
        return Object.freeze(rv);
    }

    updateProps(newProps) {
        const eventSummary = new AgEventSummary();
        Object.assign(eventSummary, this, newProps);
        return Object.freeze(eventSummary);
    }
}
